import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
} from '@mui/material';
import { nonMemberFamilyService } from '../api/services/nonMemberFamilyService';
import { useSnackbar } from './common/SnackbarProvider';

interface NonMemberFamilyDialogProps {
  open: boolean;
  /** The non-member (Member row) the new family member is attached to. */
  nonMemberId: string;
  nonMemberName?: string;
  onClose: () => void;
  /** Called after a successful add so the parent can refetch. */
  onAdded: () => void;
}

// Mirrors the relationship_type values accepted by the NonMemberFamily API.
const RELATIONSHIPS: Array<{ value: string; label: string }> = [
  { value: 'CHILD', label: 'Child' },
  { value: 'SPOUSE', label: 'Spouse' },
  { value: 'PARTNER', label: 'Partner' },
  { value: 'PARENT', label: 'Parent' },
  { value: 'SIBLING', label: 'Sibling' },
  { value: 'OTHER', label: 'Other' },
];

const emptyForm = {
  first_name: '',
  last_name: '',
  date_of_birth: '',
  email: '',
  phone: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
};

const NonMemberFamilyDialog: React.FC<NonMemberFamilyDialogProps> = ({
  open,
  nonMemberId,
  nonMemberName,
  onClose,
  onAdded,
}) => {
  const snackbar = useSnackbar();
  const [relationship, setRelationship] = useState('CHILD');
  const [form, setForm] = useState({ ...emptyForm });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setRelationship('CHILD');
    setForm({ ...emptyForm });
    setError(null);
  };
  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError('First and last name are required.');
      return;
    }
    setError(null);
    setSubmitting(true);

    try {
      await nonMemberFamilyService.create(nonMemberId, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        date_of_birth: form.date_of_birth || undefined,
        relationship_type: relationship,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        emergency_contact_name: form.emergency_contact_name.trim() || undefined,
        emergency_contact_phone: form.emergency_contact_phone.trim() || undefined,
      });
      snackbar.success('Family member added.');
      reset();
      onAdded();
      onClose();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Failed to add family member'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Family Member{nonMemberName ? ` — ${nonMemberName}` : ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            Adds a family member to this non-member's account.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Relationship</InputLabel>
            <Select
              label="Relationship"
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
            >
              {RELATIONSHIPS.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              required
              label="First Name"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            />
            <TextField
              fullWidth
              required
              label="Last Name"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            />
          </Box>
          <TextField
            fullWidth
            type="date"
            label="Date of Birth"
            InputLabelProps={{ shrink: true }}
            value={form.date_of_birth}
            onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
          />
          <TextField
            fullWidth
            type="email"
            label="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <TextField
            fullWidth
            label="Phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">Emergency contact (optional)</Typography>
          </Divider>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="Contact name"
              value={form.emergency_contact_name}
              onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Contact phone"
              placeholder="(555) 123-4567"
              value={form.emergency_contact_phone}
              onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? 'Adding…' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NonMemberFamilyDialog;
